import { useState, useMemo } from 'react';

interface Column<T> {
  key: string;
  // MKS-332: `ReactNode`, not `string`, so a money column can carry a
  // `<MoneyMetricLabel>` — its name plus the definition tooltip — instead of
  // a bare caption. The `<th>` already has Tailwind's `group`, which is what
  // the label's `group-hover:` tooltip hangs off.
  header: React.ReactNode;
  align?: 'left' | 'right';
  render: (row: T) => React.ReactNode;
  sortValue?: (row: T) => string | number;
}

type SortDirection = 'asc' | 'desc';

interface SortState {
  key: string;
  direction: SortDirection;
}

interface ReportTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowKey: (row: T) => string | number;
  getRowClassName?: (row: T) => string;
  footer?: React.ReactNode;
  defaultSort?: SortState;
}

function SortIcon({ direction, active }: { direction?: SortDirection; active: boolean }) {
  if (!active) {
    return (
      <svg className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
      </svg>
    );
  }

  if (direction === 'asc') {
    return (
      <svg className="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    );
  }

  return (
    <svg className="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

export default function ReportTable<T>({ columns, rows, getRowKey, getRowClassName, footer, defaultSort }: ReportTableProps<T>) {
  const [sort, setSort] = useState<SortState | null>(defaultSort ?? null);

  const handleSort = (column: Column<T>) => {
    if (!column.sortValue) return;

    setSort((current) => {
      if (current?.key === column.key) {
        if (current.direction === 'asc') {
          return { key: column.key, direction: 'desc' };
        }
        return null; // Remove sort on third click
      }
      return { key: column.key, direction: 'asc' };
    });
  };

  const sortedRows = useMemo(() => {
    if (!sort) return rows;

    const column = columns.find((c) => c.key === sort.key);
    if (!column?.sortValue) return rows;

    return [...rows].sort((a, b) => {
      const aVal = column.sortValue!(a);
      const bVal = column.sortValue!(b);

      let comparison = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.localeCompare(bVal);
      } else {
        comparison = (aVal as number) - (bVal as number);
      }

      return sort.direction === 'asc' ? comparison : -comparison;
    });
  }, [rows, sort, columns]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((col) => {
                const isSortable = !!col.sortValue;
                const isActive = sort?.key === col.key;

                return (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col)}
                    className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      col.align === 'right' ? 'text-right' : 'text-left'
                    } ${isSortable ? 'cursor-pointer select-none group hover:bg-gray-100' : ''}`}
                  >
                    <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                      <span>{col.header}</span>
                      {isSortable && <SortIcon direction={sort?.direction} active={isActive} />}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-8 text-center text-sm text-gray-500">
                  No data available for the selected filters.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => (
                <tr key={getRowKey(row)} className={`hover:bg-gray-50 ${getRowClassName?.(row) ?? ''}`}>
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-6 py-3 whitespace-nowrap text-sm ${
                        col.align === 'right' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          {footer && (
            <tfoot className="bg-gray-50 border-t border-gray-200">
              {footer}
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
